import { clientEnv } from "../env";
import type { OmnisyniaPlan } from "./auth.types";

export type PurchasablePlan = "pro" | "ultra";
export type BillingMode = "test" | "live" | "unconfigured";

export interface BillingPlan {
  readonly id: "free" | PurchasablePlan;
  readonly name: string;
  readonly monthlyPriceCents: number;
  readonly description: string;
  readonly features: readonly string[];
}

export interface BillingCatalog {
  readonly checkoutEnabled: boolean;
  readonly mode: BillingMode;
  readonly currency: string;
  readonly plans: readonly BillingPlan[];
}

export interface BillingSubscription {
  readonly plan: PurchasablePlan;
  readonly status: string;
  readonly cancelAtPeriodEnd: boolean;
  readonly currentPeriodEnd?: string;
  readonly manageable: boolean;
}

export interface BillingStatus {
  readonly founder: boolean;
  readonly effectivePlan: OmnisyniaPlan;
  readonly subscription: BillingSubscription | null;
}

async function readJson<T>(response: Response): Promise<T> {
  const payload = await response.json() as T & { error?: string };
  if (!response.ok) throw new Error(payload.error ?? "No se pudo completar la operación de facturación.");
  return payload;
}

function authHeaders(token: string): HeadersInit {
  return { "Content-Type": "application/json", Authorization: `Bearer ${token}` };
}

export async function getBillingCatalog(): Promise<BillingCatalog> {
  return readJson<BillingCatalog>(await fetch(`${clientEnv.apiBaseUrl}/billing/plans`));
}

export async function getBillingStatus(token: string): Promise<BillingStatus> {
  return readJson<BillingStatus>(await fetch(`${clientEnv.apiBaseUrl}/billing/subscription`, {
    headers: authHeaders(token)
  }));
}

export async function startBillingCheckout(token: string, plan: PurchasablePlan): Promise<string> {
  const result = await readJson<{ url: string }>(await fetch(`${clientEnv.apiBaseUrl}/billing/checkout`, {
    method: "POST",
    headers: authHeaders(token),
    body: JSON.stringify({ plan })
  }));
  return result.url;
}

export async function openBillingPortal(token: string): Promise<string> {
  const result = await readJson<{ url: string }>(await fetch(`${clientEnv.apiBaseUrl}/billing/portal`, {
    method: "POST",
    headers: authHeaders(token)
  }));
  return result.url;
}
